import { createApi, BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { AxiosRequestConfig, AxiosError, Method } from 'axios'
import { HttpService } from '@afterpaytouch/portal-api/api'
import { Money, PaymentType } from '@afterpay/types'

// Create Checkout API Types
export interface AppleGatewayCheckoutRequest {
  sessionId: string
  verifier?: string
  checkoutAmount?: Money // Optional: For in-store flows where user adjusts amount
  isCheckoutAdjustment?: boolean // For new flow: indicates amount adjustment after checkout creation
}

export interface AppleGatewayCheckoutResponse {
  token: string
  expires: string
  redirectCheckoutUrl: string
  appleRedirectLink: string
  consumerId?: string
}

// Save Offer API Types
export type UserCohort = 'AFTERPAY' | 'CASHAPP'

export type Period = 'daily' | 'monthly'

export type DeclineCode = 'INSUFFICIENT_CREDIT_LIMIT' | 'ACCOUNT_RESTRICTED' | 'USER_NOT_ELIGIBLE' | 'RISK_DECLINE' | 'FRAUD_DECLINE'

export interface DownPayment extends Money {
  dueBy: string
}

export interface InterestDetails {
  aprInterest: {
    percentage: string
    totalInterest: Money
  }
}

export interface PresentedOffer {
  offerId: string
  numberOfInstallments: number
  recurringInstallmentAmount: Money
  offerType: PaymentType.PBI | PaymentType.PCL
  period: Period
  total: Money
  interval: number
  downPayment: DownPayment
  firstInstallmentDueDate: string
  finalInstallmentAmount: Money
  interestDetails?: InterestDetails
}

export interface OfferDetails {
  presentedOffer: PresentedOffer[]
  selectedOfferId: string
  selectedOfferLink: string
  validUntil?: string
}

export interface AppleGatewaySaveOfferRequestAccepted {
  token: string
  userWasDeclined: false
  userCohort: UserCohort
  userId: string
  offerDetails: OfferDetails
  phoneNumber: string
}

export interface AppleGatewaySaveOfferRequestDeclined {
  token: string
  userWasDeclined: true
  userCohort: UserCohort
  userId: string
  declinedCode: DeclineCode
}

export type AppleGatewaySaveOfferRequest = AppleGatewaySaveOfferRequestAccepted | AppleGatewaySaveOfferRequestDeclined

export interface AppleGatewaySaveOfferResponse {
  appleWalletRedirectLink?: string
}

// Issue Card API Types
export interface Consumer {
  consumerUuid: string
}

export interface AppleGatewayIssueCardRequest {
  token: string
  consumer: Consumer
}

export interface AppleGatewayIssueCardResponse {
  appleWalletRedirectLink?: string
}

export class AppleGatewayService {
  public baseQueryFn: HttpService | null = null

  readonly getBaseQuery: BaseQueryFn<
    {
      url: string
      method: Method
      data?: AxiosRequestConfig['data']
      headers?: Record<string, string>
    },
    unknown,
    FetchBaseQueryError,
    {
      timeout?: AxiosRequestConfig['timeout']
    }
  > = async (args, _api, opts = {}) => {
    const { url, method, data, headers } = args
    const { timeout } = opts

    try {
      if (this.baseQueryFn === null) {
        throw new Error('Apple Gateway baseQueryFn not set')
      }
      const result = await this.baseQueryFn.request(url, method, data ?? null, headers, timeout)
      return { data: result }
    } catch (e) {
      const error = e as AxiosError & Error
      const response = error.response?.data ?? error.message ?? null
      return {
        error: {
          status: error.response?.status ?? 'FETCH_ERROR',
          data: response,
        } as FetchBaseQueryError,
      }
    }
  }

  public setBaseQueryFn(queryFn: HttpService): AppleGatewayService {
    this.baseQueryFn = queryFn
    return this
  }

  readonly service = createApi({
    reducerPath: 'appleGatewayApi',
    baseQuery: this.getBaseQuery,
    endpoints: (build) => ({
      createCheckout: build.mutation<AppleGatewayCheckoutResponse, AppleGatewayCheckoutRequest>({
        query: (request) => ({
          url: '/portal/consumers/checkout/create',
          method: 'POST',
          data: request,
        }),
      }),
    }),
  })
}

export const appleGatewayService = new AppleGatewayService()

/**
 * RTK Query hooks for Apple Gateway Service API
 *
 * @example
 * // Create checkout
 * const [createCheckout, { isLoading, error }] = useCreateCheckoutMutation()
 * const result = await createCheckout({
 *   sessionId: 'session-123',
 *   verifier: 'verifier-abc'
 * })
 * // Returns: { token, expires, redirectCheckoutUrl, appleRedirectLink }
 */
export const { useCreateCheckoutMutation } = appleGatewayService.service

export const CREATE_CHECKOUT_FIXED_KEY = 'apple-paylater-create-checkout'
